import { useState, useEffect } from 'react'
import { ConversationList } from '@/components/inbox/ConversationList'
import { ChatWindow } from '@/components/inbox/ChatWindow'
import { useProgramContext } from '@/context/ProgramContext'
import { Mailbox } from 'lucide-react'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { InboxSkeleton } from '@/components/inbox/InboxSkeleton'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useTranslation } from '@/hooks/useTranslation'

export default function Inbox() {
	const { t } = useTranslation()
	const [searchParams, setSearchParams] = useSearchParams()
	const [selectedConversationId, setSelectedConversationId] = useState<
		string | null
	>(null)
	const {
		currentProgram,
		loading: isProgramLoading,
		currentProgramAccessLevel,
	} = useProgramContext()
	const isMobile = useIsMobile()
	const navigate = useNavigate()

	useEffect(() => {
		const conversationIdFromUrl = searchParams.get('conversation')
		if (conversationIdFromUrl) {
			setSelectedConversationId(conversationIdFromUrl)
		}
	}, [searchParams])

	const { data: conversations = [], isLoading: conversationsLoading } =
		useQuery({
			queryKey: ['conversations', currentProgram?.id],
			queryFn: () =>
				apiClient(`/api/chat/conversations?programId=${currentProgram?.id}`),
			enabled: !!currentProgram?.id,
		})

	useEffect(() => {
		const conversationIdFromUrl = searchParams.get('conversation')
		if (!selectedConversationId && !conversationIdFromUrl && conversations.length > 0) {
			const sortedConversations = [...conversations].sort((a, b) => {
				const dateA = new Date(a.last_message_at || a.created_at).getTime()
				const dateB = new Date(b.last_message_at || b.created_at).getTime()
				return dateB - dateA
			})
			const lastConversation = sortedConversations[0]
			if (lastConversation) {
				setSelectedConversationId(lastConversation.id)
				setSearchParams({ conversation: lastConversation.id })
			}
		}
	}, [conversations, selectedConversationId, searchParams, setSearchParams])

	const handleSelectConversation = (conversationId: string) => {
		setSelectedConversationId(conversationId)
		// Update URL with conversation ID
		setSearchParams({ conversation: conversationId })
	}

	const handleBack = () => {
		setSelectedConversationId(null)
		// Clear conversation ID from URL
		setSearchParams({})
	}

	const handleConversationDeleted = () => {
		setSelectedConversationId(null)
		// Clear conversation ID from URL
		setSearchParams({})
	}

	if (isProgramLoading || !currentProgram) {
		return <InboxSkeleton />
	}

	// Show read-only message for users with read access
	if (currentProgramAccessLevel === 'read') {
		return (
			<div className="flex h-full items-center justify-center p-4">
				<div className="text-center max-w-md">
					<Mailbox className="h-16 w-16 text-muted-foreground/50 mb-4 mx-auto" />
					<h2 className="text-lg font-semibold text-foreground mb-2">
						{t('inbox.readOnlyAccess')}
					</h2>
					<p className="text-muted-foreground">
						{t('inbox.readOnlyAccessMessage')}
					</p>
				</div>
			</div>
		)
	}

	return (
		<div className="inbox-container h-full flex relative overflow-hidden p-0 m-0 box-border" style={{ padding: 0, margin: 0 }}>
			{/* Mobile: Show as full-screen panels, Desktop: Show side-by-side */}
			<aside
				className={cn(
					'inbox-aside flex-shrink-0 box-border',
					// Desktop layout - using exact pixel calculations
					'md:w-[340px] md:min-w-[340px] md:max-w-[340px] lg:w-[380px] lg:min-w-[380px] lg:max-w-[380px] md:relative md:translate-x-0',
					// Mobile layout - full width, absolute positioning
					'w-full absolute inset-0 z-20 md:z-auto',
					// Mobile transitions
					'transition-transform duration-300 ease-in-out md:transition-none',
					isMobile && selectedConversationId
						? '-translate-x-full'
						: 'translate-x-0'
				)}>
				<ConversationList
					programId={currentProgram.id}
					selectedConversationId={selectedConversationId}
					onSelectConversation={handleSelectConversation}
				/>
			</aside>
			<main
				className={cn(
					'inbox-main flex-1 flex flex-col overflow-hidden',
					'absolute inset-0 md:relative',
					// Mobile transitions
					'transition-transform duration-300 ease-in-out md:transition-none',
					isMobile && !selectedConversationId
						? 'translate-x-full'
						: 'translate-x-0'
						)}>
						{conversationsLoading ? (
					<div className="h-full flex flex-col items-center justify-center text-center p-4 sm:p-8">
							<div className="flex flex-col items-center gap-4">
								<Mailbox className="h-16 w-16 text-muted-foreground/50 animate-pulse" />
								<p className="text-muted-foreground">
									{t('inbox.loadingConversations')}
								</p>
						</div>
							</div>
						) : conversations.length === 0 ? (
					<div className="h-full flex flex-col items-center justify-center text-center p-4 sm:p-8">
							<div className="w-full max-w-lg space-y-6">
								<div className="text-center space-y-2">
									<Mailbox className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
									<h2 className="text-lg sm:text-xl font-semibold text-foreground">
										{t('inbox.welcomeToYourInbox')}
									</h2>
									<p className="text-sm text-muted-foreground">
										{t('inbox.toStartReceivingChats')}
									</p>
								</div>

								<div className="space-y-3 text-sm">
									<div className="flex items-center justify-between p-4 bg-background border rounded-lg shadow-sm">
										<span className="text-foreground">
											{t('inbox.step1EnableLiveChat')}
										</span>
										<Button
											variant="outline"
											size="sm"
											onClick={() => navigate('/widgets')}>
											{t('inbox.openWidgets')}
										</Button>
									</div>
									<div className="flex items-center justify-between p-4 bg-background border rounded-lg shadow-sm">
										<span className="text-foreground">
											{t('inbox.step2UpdateAgentProfile')}
										</span>
										<Button
											variant="outline"
											size="sm"
											onClick={() => navigate('/live-chat?tab=agent')}>
											{t('inbox.agentSettings')}
										</Button>
									</div>
									<div className="flex items-center justify-between p-4 bg-background border rounded-lg shadow-sm">
										<span className="text-foreground">
											{t('inbox.step3EmbedWidget')}
										</span>
										<Button
											variant="outline"
											size="sm"
											onClick={() => navigate('/widgets')}>
											{t('inbox.installationGuide')}
										</Button>
									</div>
								</div>
							</div>
					</div>
				) : selectedConversationId ? (
					<ChatWindow
						conversationId={selectedConversationId}
						onBack={isMobile ? handleBack : undefined}
						onConversationDeleted={handleConversationDeleted}
						visitorName={conversations.find(c => c.id === selectedConversationId)?.visitor_name}
						visitorEmail={conversations.find(c => c.id === selectedConversationId)?.visitor_email}
					/>
				) : null}
			</main>
		</div>
	)
}
